import isEqual from 'lodash-es/isEqual';
import { FreeBusyCacheManager, FreeBusyType } from 'owa-freebusy-cache';
import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { getRoomAvailability } from '../actions/calendarRoomActions';
import { CalendarEventTime, RoomAvailability } from '../components/RoomSelector/RoomSelector.types';
import { BookingType } from 'owa-location-service';
import getRoomAvailabilityStore from '../store/roomAvailabilityStore';
import { isFeatureEnabled } from 'owa-feature-flags';

/**
 * Get room availability using getRoomAvailability API or regular FB. After reading the roomAvailaivility form either API the
 * results are cached for next call for the same times.
 * @param roomEmail : the email of the room
 * @param cacheManager : FB cache manager for regular FB calls
 * @param calendarEventTime : the event times for which we request availability
 * @param bookingType: the room booking type. If the room is reserved we don't request FB from server but return that the room is busy
 * @param useGetRoomAvailabilityForLogging: This is only for logging for experiment. We fetch the availabilities for all rooms using GetRoomAvailability,
 * even when the flight 'cal-mf-availabilityForRecurrence' is off. To compare this availabilities between treatment and control.
 * TODO: Remove this when experiment is over (https://exp.microsoft.com/Rollout?id=f3d8a340-9076-46ee-8db1-d8e17e131b38)
 * @param filter: original event used for regular FB calls
 */
export default function getRoomFreeBusy(
    roomEmail: string,
    cacheManager: FreeBusyCacheManager,
    calendarEventTime: CalendarEventTime,
    bookingType?: BookingType,
    filter?: CalendarEvent,
    useGetRoomAvailabilityForLogging?: boolean
): RoomAvailability {
    // we should use getCalendarAvailability if resolve policy is enabled, or recurrence availability is enable and the event is a recurrence
    const shouldUseRoomAvailability =
        useGetRoomAvailabilityForLogging ||
        isFeatureEnabled('cal-mf-roomBookingPolicy') ||
        (isFeatureEnabled('cal-mf-availabilityForRecurrence') && calendarEventTime.Recurrence);

    if (bookingType == 'Reserved') {
        return { freeBusyState: FreeBusyType.Busy };
    } else if (shouldUseRoomAvailability) {
        return getAvailability(roomEmail, calendarEventTime, filter);
    } else {
        const freeBusy = cacheManager.getAggregatedFreeBusy(
            roomEmail,
            calendarEventTime.Start,
            calendarEventTime.End,
            calendarEventTime.IsAllDayEvent,
            filter
        );
        return { freeBusyState: freeBusy };
    }
}

function getAvailability(
    roomEmail: string,
    calendarEventTime: CalendarEventTime,
    originalEvent?: CalendarEvent
): RoomAvailability {
    /**
     * When user is editing a meeting, we don't want to count the rooms that accepted the meeting as busy.
     * The hypothesis is that if the room accpeted the meeting then it available for the duration of the meeting.
     * this is based on the assumption that most rooms don't accept more than one meeting for the the sime slot,
     * so if the room is booked for this meeting then it free if not for this meeting.
     * The drawback is that is some cases room can be configured to accept conflicting bookings: and we would consider a room
     * to be free. But even in this case considering the room available is not too bad since it accepts conflicts.
     * We need to fix this on server, but for single meetings I am adding this to client for now (same is done for free busy).
     * Recurrence is more complex and needs to be fixed on server because of policy conflic count and percentage.
     */
    if (
        !calendarEventTime.Recurrence &&
        originalEvent &&
        isEqual(calendarEventTime.Start, originalEvent.Start) &&
        isEqual(calendarEventTime.End, originalEvent.End) &&
        originalEvent.Resources &&
        originalEvent.Resources.some(resource => {
            return resource.Mailbox.EmailAddress == roomEmail && resource.ResponseType == 'Accept';
        })
    ) {
        return { freeBusyState: FreeBusyType.Free };
    }

    // Need to make the call to API if is not already made, and included into roomAvailabilityStates, for this room.
    // If the call was made just need to wait to complete, when isGetingAvailabilityForRooms switches to false
    getRoomAvailability(roomEmail, calendarEventTime);

    // Above call to getRoomAvailability() adds the roomEmail to the collection with availability set to Loading (still waiting for callback) before making the asycn call to API
    // So here try again to get the roomAvailabilitystate to be used below to find the FB status.
    const roomAvailabilityState = getRoomAvailabilityStore().roomAvailabilityStates.get(roomEmail);

    return {
        freeBusyState: roomAvailabilityState.availability,
        roomRecurrenceAvailability: roomAvailabilityState.roomRecurrenceAvailability,
    };
}
